import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionButton } from '@/components/shared/ActionButton';
import { SaveAsTemplateDialog } from '../breakdown/SaveAsTemplateDialog';
import { EditableLinkField } from '@/components/shared/EditableLinkField';
import { JobHoursModal } from './JobHoursModal';
import { JobCardPreviewModal } from './JobCardPreviewModal';
import { downloadJobCardXlsx } from '@/lib/jobCardDownload';
import { Panel } from '@/components/ui/panel';
import { Switch } from '@/components/ui/switch';
import { useArchiveJob, useDuplicateJob, useJob } from '@/hooks/useJobs';
import type { InvoiceType } from '@jobos/shared/types/invoice';
import { DropdownButtonMenu } from '@/components/ui/dropdown-button-menu';
import { useCreateInvoice } from '@/hooks/useInvoices';
import { useToast } from '@/hooks/use-toast';
import { PartialInvoiceDialog } from '../billing/PartialInvoiceDialog';
import { useRecurringScheduleByJob } from '@/hooks/useRecurringSchedules';
import { RecurringScheduleModal } from '@/pages/recurring/RecurringScheduleModal';

interface JobActionsCardProps {
  jobId: string;
  jobCode: string;
  onedrivePath: string | null | undefined;
  startDate: string | null | undefined;
  invoiceType: InvoiceType | null | undefined;
  isArchived: boolean;
  onUpdateField: (field: string, value: any) => Promise<void>;
}

export function JobActionsCard({
  jobId,
  jobCode,
  onedrivePath,
  startDate,
  invoiceType,
  isArchived,
  onUpdateField,
}: JobActionsCardProps) {
  const navigate = useNavigate();
  const [jobCardPreviewOpen, setJobCardPreviewOpen] = useState(false);
  const [isGeneratingJobCard, setIsGeneratingJobCard] = useState(false);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<'final' | 'partial' | null>(null);
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const { data: recurringSchedule } = useRecurringScheduleByJob(jobId);
  const createInvoice = useCreateInvoice(jobId);
  const { toast } = useToast();
  const { mutateAsync: archiveJob } = useArchiveJob(jobId);
  const { mutateAsync: duplicateJob, isPending: isDuplicating } = useDuplicateJob();
  const { data: job } = useJob(jobId);

  const handleDuplicateJob = async () => {
    if (!job) return;
    const newJob = await duplicateJob(job);
    navigate(`/jobs/${newJob.id}`);
  };

  // Downloads straight to the .xlsx, skipping the preview. Any non-fatal
  // warning is intentionally swallowed here — it is surfaced in the preview
  // modal instead, rather than as a popup on an otherwise successful download.
  const handleGenerateJobCard = async () => {
    setIsGeneratingJobCard(true);
    try {
      await downloadJobCardXlsx({ jobId, jobCode });
    } catch (error) {
      console.error('Error generating job card:', error);
      alert('Failed to generate job card. Please try again.');
    } finally {
      setIsGeneratingJobCard(false);
    }
  };

  const handleCalculateHours = () => {
    setHoursModalOpen(true);
  }

  const handleArchiveToggle = async () => {
    await archiveJob(!isArchived);
  }

  const handleCreateFinalInvoice = async () => {
    setIsCreatingInvoice('final');
    try {
      const invoice = await createInvoice.mutateAsync({ kind: 'final' });
      // No PDF is saved on create — the toast is the confirmation, and the
      // Billing tab's invoice row is where the file is downloaded or viewed.
      toast({ title: `Final invoice ${invoice.invoiceNumber} created` });
    } catch (error) {
      console.error('Error creating final invoice:', error);
    } finally {
      setIsCreatingInvoice(null);
    }
  };

  return (
    <Panel title="🔧 Actions" contentClassName="space-y-3">
        {/* The button and the cash toggle only fit side by side once there is
            room for both — on a phone the toggle drops onto its own row. */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[12rem] flex-1">
            <ActionButton
              label="Preview Job Card"
              onClick={() => setJobCardPreviewOpen(true)}
            />
          </div>
          <div className="flex items-center gap-1">
            <label htmlFor="cash-toggle" className="text-xs cursor-pointer whitespace-nowrap">
              Cash? - N
            </label>
            <Switch
              id="cash-toggle"
              checked={invoiceType === 'cash'}
              onCheckedChange={(checked: boolean) =>
                onUpdateField('invoiceType', checked ? 'cash' : 'quickbooks')
              }
            />
            <label htmlFor="cash-toggle" className="text-xs cursor-pointer whitespace-nowrap">
              Y
            </label>
          </div>
        </div>
        <ActionButton
          label={isGeneratingJobCard ? 'Generating...' : 'Generate Job Card'}
          onClick={() => handleGenerateJobCard()}
          disabled={isGeneratingJobCard}
        />
        <EditableLinkField
          value={onedrivePath}
          onSave={(value) => onUpdateField('onedrivePath', value)}
          addButtonLabel="➕ Add OneDrive Link"
          linkButtonLabel="📁 Open OneDrive"
          placeholder="Enter OneDrive URL..."
        />
        <ActionButton
          label="Calculate Hours"
          onClick={() => handleCalculateHours()}
          disabled={!startDate}
          title={!startDate ? "Set a start date to view hours" : undefined}
        />
        <DropdownButtonMenu
          label="Generate Invoice"
          ariaLabel="Generate invoice"
          size="default"
          className="w-full"
          items={[
            { label: isCreatingInvoice === 'final' ? 'Creating...' : 'Final Invoice', onClick: handleCreateFinalInvoice, disabled: isCreatingInvoice !== null },
            { label: isCreatingInvoice === 'partial' ? 'Creating...' : 'Partial Invoice', onClick: () => setPartialDialogOpen(true), disabled: isCreatingInvoice !== null },
          ]}
        />
        <DropdownButtonMenu
          label="More Actions"
          ariaLabel="More actions"
          size="default"
          className="w-full"
          items={[
            {
              label: isDuplicating ? 'Duplicating...' : 'Duplicate Job',
              onClick: handleDuplicateJob,
              disabled: isDuplicating || !job,
            },
            {
              label: recurringSchedule ? 'Manage Recurring Reminder' : 'Set Up Recurring Reminder',
              onClick: () => setRecurringModalOpen(true),
            },
            {
              label: 'Save Breakdown as Template',
              onClick: () => setSaveTemplateOpen(true),
              disabled: !job,
            },
            {
              label: isArchived ? 'Unarchive Job' : 'Archive Job',
              onClick: handleArchiveToggle,
            },
          ]}
        />

      <JobCardPreviewModal
        jobId={jobId}
        jobCode={jobCode}
        open={jobCardPreviewOpen}
        onOpenChange={setJobCardPreviewOpen}
      />
      <JobHoursModal
        jobId={jobId}
        jobCode={jobCode}
        open={hoursModalOpen}
        onOpenChange={setHoursModalOpen}
      />
      <PartialInvoiceDialog
        open={partialDialogOpen}
        onOpenChange={setPartialDialogOpen}
        jobId={jobId}
        jobCode={jobCode}
        jobDescription={null}
      />
      <RecurringScheduleModal
        open={recurringModalOpen}
        onOpenChange={setRecurringModalOpen}
        sourceJobId={jobId}
        existing={recurringSchedule}
      />
      <SaveAsTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        jobId={jobId}
        jobProjectType={job?.projectType ?? ''}
      />
    </Panel>
  );
}
